import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"

export default class extends Controller {
  static targets = ["modal", "modalIcon", "modalTitle", "modalMessage", "modalCloseBtn", "retryResults"]
  static values = { executeUrl: String }

  retrySingle(event) {
    const failureId = event.currentTarget.dataset.failureId
    this._showModal("Retrying Single Failure", "Attempting to retry failed operation...")

    this._postRetry(failureId)
      .then((result) => {
        this._showResult(result, "Single Failure Retry")
        if (result.success) setTimeout(() => Turbo.visit(window.location.href, { action: "replace" }), 2000)
      })
      .catch(() => {
        this._showResult({ success: false, message: "Network error occurred" }, "Single Failure Retry")
      })
  }

  retryAll() {
    if (!confirm("Are you sure you want to retry all unresolved failures?")) return

    this._showModal("Retrying All Failures", "Processing...")

    const failureIds = []
    document.querySelectorAll("tr[data-failure-id]").forEach((row) => {
      if (!row.querySelector(".bg-green-100")) failureIds.push(parseInt(row.dataset.failureId))
    })

    if (failureIds.length === 0) {
      this._showResult({ success: false, message: "No unresolved failures found" }, "Bulk Retry")
      return
    }

    const results = failureIds.map((id) => this._postRetry(id).catch(() => ({ success: false })))

    Promise.all(results).then((outcomes) => {
      const successful = outcomes.filter((r) => r.success).length
      const failed = outcomes.length - successful

      let message
      if (successful === outcomes.length) message = `All ${outcomes.length} failures retried successfully.`
      else if (successful > 0) message = `${successful} successful, ${failed} failed out of ${outcomes.length}.`
      else message = `All ${outcomes.length} retry attempts failed.`

      this._showResult({ success: successful > 0, message }, "Bulk Retry")
      setTimeout(() => Turbo.visit(window.location.href, { action: "replace" }), 3000)
    })
  }

  _postRetry(failureId) {
    return fetch(this.executeUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]').getAttribute("content"),
      },
      body: JSON.stringify({
        action_type: "retry_failed_operations",
        action_data: { retry_scope: "single", failure_id: failureId },
      }),
    }).then((response) => response.json())
  }

  _showModal(title, message) {
    this.modalTitleTarget.textContent = title
    this.modalMessageTarget.textContent = message
    this.modalCloseBtnTarget.classList.add("hidden")
    this.retryResultsTarget.classList.add("hidden")
    this.modalTarget.classList.remove("hidden")
    this.modalIconTarget.innerHTML =
      '<div class="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>'
    this.modalIconTarget.className =
      "mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100"
  }

  _showResult(result, title) {
    this.modalTitleTarget.textContent = `${title} Complete`
    this.modalMessageTarget.textContent = result.message
    this.modalCloseBtnTarget.classList.remove("hidden")

    const iconClass = result.success ? "bg-green-100" : "bg-red-100"
    const iconColor = result.success ? "text-green-600" : "text-red-600"
    const iconPath = result.success
      ? "M5 13l4 4L19 7"
      : "M6 18L18 6M6 6l12 12"

    this.modalIconTarget.className = `mx-auto flex items-center justify-center h-12 w-12 rounded-full ${iconClass}`
    this.modalIconTarget.innerHTML = `<svg class="h-6 w-6 ${iconColor}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="${iconPath}"></path></svg>`
  }

  closeModal() {
    this.modalTarget.classList.add("hidden")
  }
}
